<template>
  <div class="mt-3">
    <div
      class="px-3 text-xs-center"
      :class="{'mb-2' : $vuetify.breakpoint.xsOnly, 'mb-3': $vuetify.breakpoint.smAndUp}"
    >
      <v-btn
        :flat="year !== currentYear"
        :outline="year === currentYear"
        :color="year === currentYear ? 'primary' : ''"
        :disabled="isFetching"
        dense
        class="ma-0"
        style="min-width: 60px;"
        v-for="{date: {year}} in years"
        @click="setYear(year)"
        :key="'year-' + year"
      >
        {{year}}
      </v-btn>
    </div>
    <v-divider />
    <div
      class="px-3 text-xs-center"
      :class="{'my-2' : $vuetify.breakpoint.xsOnly, 'my-3': $vuetify.breakpoint.smAndUp}"
    >
      <v-btn
        :flat="month !== currentMonth"
        :outline="month === currentMonth"
        :color="month === currentMonth ? 'primary' : ''"
        dense
        :disabled="month.disabled || isFetching"
        style="min-width: 50px;"
        class="ma-0 pa-0"
        v-for="month in activeMonths"
        @click="setMonth(month.value)"
        :key="'month-' + month.value"
      >
        {{month.short}}
      </v-btn>
    </div>
    <v-divider />

    <v-progress-linear
      :indeterminate="isFetching"
      class="ma-0 pa-0"
      :color="isFetching ? 'primary' : 'transparent'"
    />

    <v-container v-if="$vuetify.breakpoint.smAndUp">
      <v-layout justify-center>
        <v-flex
          xl6
          lg8
          md10
        >
          <v-sheet
            elevation="3"
            color="grey lighten-4"
            dark
            class="ma-3 pa-2"
          >
            <chartist
              ref="chart"
              ratio="ct-major-second"
              type="Bar"
              :responsive-options="this.currentMonth ? responsiveOptions : []"
              :options="chartOptions"
              :data="chartData()"
            ></chartist>
          </v-sheet>
        </v-flex>
      </v-layout>
    </v-container>

    <v-sheet
      v-else
      elevation="3"
      color="grey lighten-4"
      dark
      class="ma-3 pa-2"
    >
      <chartist
        ref="chart"
        ratio="ct-major-second"
        type="Bar"
        :responsive-options="this.currentMonth ? responsiveOptions : []"
        :options="chartOptions"
        :data="chartData()"
      ></chartist>
    </v-sheet>
  </div>
</template>

<script>
import moment from 'moment';
import Chartist from 'chartist';

export default {
  data() {
    return {
      views: ['day', 'month', 'year'],
      currentYear: new Date().getFullYear(),
      selectedMonth: 0,
      responsiveOptions: [
        ['screen and (max-width: 450px)', {
          classNames: {
            bar: 'ct-bar ct-bar--thin',
          },
        }],
      ],
    };
  },

  async created() {
    this.setMonth(new Date().getMonth());
  },

  computed: {
    isFetching() {
      const yields = this.$store.getters['yields/yield'](this.currentYear);

      return !yields || yields.isFetching || this.$store.state.yields.overview.isFetching;
    },

    yields() {
      return this.$store.state.yields.yields;
    },

    years() {
      return (this.yields.length && this.yields) || [{ year: new Date().getFullYear() }];
    },

    chartData() {
      return () => ({
        labels: this.getLabels(),
        series: [this.getSeries()],
      });
    },

    months() {
      let date = moment();

      return new Array(12).fill(null).map((o, i) => {
        date = date.month(i);

        return {
          short: date.format('MMM'), text: date.format('MMMM'), value: i, disabled: false,
        };
      });
    },

    activeMonths() {
      const yields = this.$store.getters['yields/yield'](this.currentYear);

      if (!yields || !yields.year) {
        return this.months;
      }

      return this.months.map(month => (
        { ...month, disabled: !yields.year.find(y => y.month === month.value) }
      ));
    },

    currentMonth() {
      return this.activeMonths[this.selectedMonth];
    },

    chartOptions() {
      return {
        lineSmooth: true,
        showArea: true,
        chartPadding: {
          top: 20,
          right: 0,
          bottom: 20,
          left: 25,
        },
        axisY: {
          onlyInteger: true,
          offset: 20,
        },
        plugins: [
          Chartist.plugins.ctAxisTitle({
            axisX: {
              axisTitle: () => (this.currentMonth ? `${this.currentYear} - ${this.currentMonth.text}` : this.currentYear),
              offset: {
                x: 0,
                y: 40,
              },
              textAnchor: 'middle',
            },
            axisY: {
              axisTitle: 'kWh',
              offset: {
                x: 0,
                y: -5,
              },
              flipTitle: false,
            },
          }),
        ],
      };
    },
  },

  methods: {
    getSeries() {
      const yields = this.$store.getters['yields/yield'](this.currentYear);

      if (!yields || (this.currentMonth && !yields.month) || (!this.currentMonth && !yields.year)) {
        return [];
      }

      if (!this.currentMonth) {
        return new Array(12).fill(null).map((o, i) => {
          const monthlyYield = yields.year.find(({ month }) => month === i);

          if (monthlyYield) {
            return monthlyYield.yield / 1000;
          }

          return undefined;
        });
      }

      const currentMonth = this.currentMonth.value;
      const date = moment().month(currentMonth).year(this.currentYear).date(1);

      return new Array(date.daysInMonth()).fill(null).map((o, i) => {
        // eslint-disable-next-line max-len
        const dailyYield = yields.month.find(({ day, month }) => currentMonth === month && day === (i + 1));

        if (dailyYield) {
          return dailyYield.yield / 1000;
        }

        return undefined;
      });
    },

    getLabels() {
      if (!this.currentMonth) {
        return this.$vuetify.breakpoint.xsOnly ? moment.monthsShort() : moment.months();
      }

      const days = moment().month(this.currentMonth.value).year(this.currentYear).date(1);

      return new Array(days.daysInMonth()).fill(null).map((o, i) => {
        if (i % 2 === 0 || this.$vuetify.breakpoint.smAndUp) {
          return i + 1;
        }

        return '';
      });
    },

    async setMonth(month) {
      if (month === this.selectedMonth) {
        this.selectedMonth = null;
      } else {
        this.selectedMonth = month;
        await this.$store.dispatch('yields/getYields', { year: this.currentYear, period: 'month', month: this.currentMonth.value });
      }
    },

    async setYear(year) {
      this.currentYear = year;

      if (this.selectedMonth >= 0 && this.activeMonths[this.selectedMonth]) {
        if (this.activeMonths[this.selectedMonth].disabled) {
          this.selectedMonth = this.activeMonths.findIndex(m => !m.disabled);
        }

        await this.$store.dispatch('yields/getYields', { year, period: 'month', month: this.currentMonth.value });
      } else {
        await this.$store.dispatch('yields/getYields', { year, period: 'year' });
      }
    },
  },
};
</script>
